import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGithub, FaExternalLinkAlt, FaCode, FaRocket, FaBrain, FaBookOpen } from 'react-icons/fa';
import CaseStudyModal from './CaseStudyModal';
import DocFlowModal from './DocFlowModal';
import styles from './Projects.module.css';

const Projects = () => {
  const projects = [
    {
      title: 'Docu-Flow',
      category: 'Saas / Automation',
      tech: ['React', 'Node.js', 'Socket.io'],
      desc: 'Streamlined document collection system reducing manual workload by 60%.',
      code: 'https://github.com/princedesai012/Doc-Flow.git',
      demo: '#',
      color: 'linear-gradient(135deg, #a855f7, #6366f1)', // Purple Theme
      featured: true,
      hasCaseStudy: true
    },
    {
      title: 'HighQ Classes',
      category: 'Management',
      tech: ['MERN Stack', 'Redux'],
      desc: 'Complete ERP solution for coaching institutes with role-based access.',
      code: '#',
      demo: '#',
      color: 'linear-gradient(135deg, #06b6d4, #3b82f6)', // Blue Theme
      featured: true
    },
    {
      title: 'CafeChain',
      category: 'Web3 / DApp',
      tech: ['Solidity', 'React', 'Ethers.js', 'Hardhat'],
      desc: 'A decentralized coffee-shop management system. It leverages smart contracts for transparent transactions and loyalty rewards program.',
      code: 'cafechain.in', // Verify link
      demo: 'cafechain.in',
      color: 'linear-gradient(135deg, #6366f1, #4f46e5)', // Indigo/Blue
      featured: true
    },
    {
      title: 'MediaWala Bot',
      category: 'Automation Bot',
      tech: ['Python', 'Telegram API', 'AWS Lambda'],
      desc: 'High-performance media processing bot handling 10k+ daily requests. Automates video downloading and compression from 15+ social platforms.',
      code: '#',
      demo: 'https://t.me/p1f_2_bot',
      color: 'linear-gradient(135deg, #3b82f6, #06b6d4)', // Cyan-Blue
      featured: false
    },
    {
      title: 'Reality Search',
      category: 'Search Engine',
      tech: ['Python', 'Elasticsearch', 'NLP'],
      desc: 'Privacy-focused search engine that filters noise. Uses NLP to aggregate precise results from verified sources, bypassing ad-heavy algorithms.',
      code: '#',
      demo: '#',
      color: 'linear-gradient(135deg, #10b981, #059669)', // Matrix Green
      featured: false
    },
    {
      title: 'GitAid',
      category: 'Dev Tool / CLI',
      tech: ['Node.js', 'OpenAI API', 'Commander'],
      desc: 'An AI-powered CLI assistant that automates git workflows. Generates semantic commit messages and PR descriptions instantly, saving developer time.',
      code: 'https://github.com/princedesai012/GitAid---PR-Code-Review.git',
      demo: '#',
      color: 'linear-gradient(135deg, #f97316, #ea580c)', // Bright Orange
      featured: true
    },

    {
      title: 'DocTime',
      category: 'HealthTech',
      tech: ['Flutter', 'Firebase'],
      desc: 'Doctor appointment booking app with live queue tracking.',
      code: 'https://github.com/princedesai012/doctor.git',
      demo: '#',
      color: 'linear-gradient(135deg, #10b981, #059669)', // Green Theme
      featured: false
    },
    {
      title: 'tweetX',
      category: 'Social',
      tech: ['Django', 'Python', 'PostgreSQL'],
      desc: 'Micro-blogging platform with real-time features.',
      code: 'https://github.com/princedesai012/tweetX.git',
      demo: '#',
      color: 'linear-gradient(135deg, #f59e0b, #d97706)', // Orange Theme
      featured: false
    },
  ];

  const [showAll, setShowAll] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  
  const displayedProjects = showAll ? projects : projects.slice(0, 4);

  // Spotlight Effect Logic
  const handleMouseMove = (e) => {
    const { currentTarget: target } = e;
    const rect = target.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    target.style.setProperty("--mouse-x", `${x}px`);
    target.style.setProperty("--mouse-y", `${y}px`);
  };

  return (
    <section id="projects" className={styles.projectSection}>
      <div className={styles.container}>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className={styles.header}
        >
          <div className={styles.badge}>
            <FaRocket /> Portfolio Highlights
          </div>
          <h2 className="text-gradient">Featured Projects</h2>
          <p className={styles.subtitle}>
            Ideas transformed into <span className={styles.highlight}>Reality</span>.
          </p>
        </motion.div>

        {/* --- HERO AI PROJECT CARD --- */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className={styles.heroCard}
        >
          <div className={styles.heroBgGlow}></div>
          
          <div className={styles.heroContent}>
            <div className={styles.heroBadge}>🏆 IBM Certified Project</div>
            <h3 className={styles.heroTitle}>Resumia AI</h3>
            <p className={styles.category} style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaBrain /> Autonomous Career Intelligence Agent
            </p>
            
            <p className={styles.heroDesc}>
              An autonomous multi-agent system that analyzes resumes, simulates ATS environments using Vector Search (RAG), and generates strategic career roadmaps. Powered by LangChain and the Gemini API.
            </p>

            <div className={styles.techStack} style={{ marginBottom: '2rem' }}>
              {['React 18', 'FastAPI', 'LangChain', 'RAG', 'FAISS', 'Gemini'].map((t, i) => (
                <span key={i} className={styles.tag}>{t}</span>
              ))}
            </div>

            <div className={styles.heroButtons}>
              <button 
                className={styles.primaryBtn}
                onClick={() => setIsModalOpen(true)}
              >
                <FaBookOpen /> Read Architecture Case Study
              </button>
              <a href="https://github.com/princedesai012/Resumia.git" target="_blank" rel="noreferrer" className={styles.secondaryBtn}>
                <FaGithub /> GitHub
              </a>
            </div>
          </div>

          <div className={styles.heroVisual}>
            <div className={styles.architecturePlaceholder}>
              <code>
                [ RAG Engine ]<br/>
                ↓<br/>
                FAISS Vector DB<br/>
                ↓<br/>
                ParserAgent → ATSAgent → FeedbackAgent
              </code>
            </div>
          </div>
        </motion.div>

        <div className={styles.grid}>
          <AnimatePresence>
            {displayedProjects.map((project, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                onMouseMove={handleMouseMove}
                className={`${styles.card} ${project.featured ? styles.featuredCard : ''}`}
              >
                {/* Spotlight Gradient Layer */}
                <div className={styles.spotlight}></div>

                {/* Card Content */}
                <div className={styles.cardContent}>

                  <div className={styles.cardHeader}>
                    <div
                      className={styles.projectIcon}
                      style={{ background: project.color }}
                    >
                      <FaCode />
                    </div>
                    <div className={styles.actions}>
                      {project.hasCaseStudy && (
                        <button onClick={() => setIsDocModalOpen(true)} className={styles.actionBtn} title="Read Case Study">
                           <FaBookOpen />
                        </button>
                      )}
                      <a href={project.code} target="_blank" rel="noreferrer" className={styles.actionBtn}>
                        <FaGithub />
                      </a>
                      {project.demo !== '#' && (
                        <a href={project.demo} target="_blank" rel="noreferrer" className={styles.actionBtn}>
                          <FaExternalLinkAlt />
                        </a>
                      )}
                    </div>
                  </div>

                  <div className={styles.textContent}>
                    <h3 className={styles.title}>{project.title}</h3>
                    <p className={styles.category}>{project.category}</p>
                    <p className={styles.desc}>{project.desc}</p>
                  </div>

                  <div className={styles.techStack}>
                    {project.tech.map((t, i) => (
                      <span key={i} className={styles.tag}>{t}</span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        <div className={styles.btnWrapper}>
          <button onClick={() => setShowAll(!showAll)} className={styles.viewMoreBtn}>
            {showAll ? 'Show Less' : 'View All Projects'}
          </button>
        </div>
        
        {/* Modal Overlays */}
        <CaseStudyModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
        <DocFlowModal isOpen={isDocModalOpen} onClose={() => setIsDocModalOpen(false)} />
      </div>
    </section>
  );
};

export default Projects;